import type { EngineConfig, EngineData, HeuristicStyle, Species } from "@kalaporssi/engine";
import { z } from "zod";
import aiPlayersJson from "../../../seed/ai-players.json";
import gameConfigJson from "../../../seed/game-config.json";
import monthsJson from "../../../seed/months.fi.json";
import placesJson from "../../../seed/places.json";
import speciesJson from "../../../seed/species.json";

// Seed data is validated once at module load so a broken seed fails fast (tests, cold start, dev server).

const speciesSchema = z.object({
  id: z.string().regex(/^[a-z]+$/),
  name: z.string().min(1),
  refPrice: z.number().positive(),
  elasticity: z.number().positive(),
  volatility: z.number().min(0),
  baseKg: z.number().positive(),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
  season: z.array(z.number().positive()).length(12),
  note: z.string(),
});

const engineConfigSchema = z.object({
  months: z.number().int().positive(),
  catchesPerRound: z.number().int().positive(),
  targetValue: z.number().positive(),
  targetJitter: z.number().min(0).max(1),
  biasFactor: z.number().min(0),
  priceCapLow: z.number().positive(),
  priceCapHigh: z.number().positive(),
  feedPrice: z.number().min(0),
  catchSizeCdf: z.array(z.number().min(0).max(1)).min(1),
  kgRandMin: z.number().positive(),
  kgRandSpan: z.number().min(0),
  kgRound: z.number().positive(),
  minKg: z.number().positive(),
  forecastRound: z.number().positive(),
  minForecast: z.number().min(0),
  actualDemandFloor: z.number().min(0),
});

const gameSettingsSchema = z.object({
  codeLength: z.number().int().positive(),
  codeAlphabet: z.string().min(2),
  maxHumanPlayers: z.number().int().positive(),
  nicknameMinLength: z.number().int().positive(),
  nicknameMaxLength: z.number().int().positive(),
  ttlDays: z.number().int().positive(),
  clientPollIntervalSeconds: z.number().positive(),
  hostForceAiAfterSeconds: z.number().int().min(0),
});

const costControlSchema = z.object({
  dailyCapUsd: z.number().positive(),
  gamesPerHostPerDay: z.number().int().positive(),
  reserveInputTokens: z.number().int().positive(),
  reserveOutputTokens: z.number().int().positive(),
});

const aiPlayerSchema = z.object({
  id: z.string().regex(/^ai_[a-z0-9]+$/),
  name: z.string().min(1),
  heuristicStyle: z.enum(["direct", "second-order"]),
  provider: z.enum(["bedrock", "anthropic"]),
  anthropicModelId: z.string().min(1),
  bedrockInferenceProfileId: z.string().min(1),
  thinkingBudgetTokens: z.number().int().min(0),
  maxOutputTokens: z.number().int().positive(),
  pricing: z.object({ inputPerMTok: z.number().min(0), outputPerMTok: z.number().min(0) }),
});

export type GameSettings = z.infer<typeof gameSettingsSchema>;
export type CostControl = z.infer<typeof costControlSchema>;
export type AiPlayerConfig = z.infer<typeof aiPlayerSchema> & { heuristicStyle: HeuristicStyle };

export const species: readonly Species[] = z.array(speciesSchema).min(1).parse(speciesJson.species);
export const places: readonly string[] = z.array(z.string().min(1)).parse(placesJson.places);
export const months: readonly string[] = z.array(z.string().min(1)).length(12).parse(monthsJson.months);
export const engineConfig: EngineConfig = engineConfigSchema.parse(gameConfigJson.engine);
export const gameSettings: GameSettings = gameSettingsSchema.parse(gameConfigJson.game);
export const costControl: CostControl = costControlSchema.parse(gameConfigJson.costControl);
export const aiPlayers: readonly AiPlayerConfig[] = z.array(aiPlayerSchema).parse(aiPlayersJson.aiPlayers);

if (places.length < engineConfig.catchesPerRound) {
  throw new Error(`seed: need at least ${engineConfig.catchesPerRound} places, got ${places.length}`);
}
if (engineConfig.months !== months.length) {
  throw new Error(`seed: engine.months (${engineConfig.months}) must match months.fi.json (${months.length})`);
}

export const engineData: EngineData = { species, places, cfg: engineConfig, months };

export const speciesById: Readonly<Record<string, Species>> = Object.fromEntries(species.map((s) => [s.id, s]));

export const aiPlayerIds = aiPlayers.map((a) => a.id) as [string, ...string[]];

export function aiPlayerById(id: string): AiPlayerConfig | undefined {
  return aiPlayers.find((a) => a.id === id);
}
